import { useState, useEffect, useRef, useCallback } from 'react';

const WS_URL = 'wss://advanced-trade-ws.coinbase.com';
const REST_URL = 'https://api.coinbase.com/v2/prices/BTC-USD/spot';
const STATS_URL = 'https://api.exchange.coinbase.com/products/BTC-USD/stats';
const REST_POLL_INTERVAL = 30000;
const STATS_POLL_INTERVAL = 600000;
const WS_RECONNECT_DELAY = 3000;

export const useBtcPrice = () => {
  const [price, setPrice] = useState(null);
  const [prevPrice, setPrevPrice] = useState(null);
  const [stats, setStats] = useState(null); 
  const [loading, setLoading] = useState(true);

  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const fallbackTimerRef = useRef(null);
  const statsTimerRef = useRef(null);
  const abortRef = useRef(null);
  const statsAbortRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchStats = useCallback(async () => {
    if (statsAbortRef.current) statsAbortRef.current.abort();
    const controller = new AbortController();
    statsAbortRef.current = controller;

    try {
      const res = await fetch(STATS_URL, { signal: controller.signal });
      if (!res.ok || !mountedRef.current) return;
      const json = await res.json();
      setStats({
        open: parseFloat(json.open),
        high: parseFloat(json.high),
        low: parseFloat(json.low),
        volume: parseFloat(json.volume),
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.warn('BTC stats fetch failed:', err.message);
      }
    }
  }, []);

  const fetchRest = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(REST_URL, { signal: controller.signal });
      if (!res.ok) return;
      const json = await res.json();
      const newPrice = parseFloat(json.data.amount);
      if (isNaN(newPrice) || !mountedRef.current) return;

      setPrice(prev => {
        setPrevPrice(prev);
        return newPrice;
      });
      setLoading(false);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.warn('BTC REST fetch failed:', err.message);
      }
    }
  }, []);

  const connectWs = useCallback(() => {
    if (!mountedRef.current) return;

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({
        type: 'subscribe',
        product_ids: ['BTC-USD'],
        channel: 'ticker',
      }));
      ws.send(JSON.stringify({
        type: 'subscribe',
        channel: 'heartbeats',
      }));

      if (fallbackTimerRef.current) {
        clearInterval(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };

    ws.onmessage = (event) => {
      if (!mountedRef.current) return;
      try {
        const data = JSON.parse(event.data);
        if (data.channel === 'ticker' && data.events) {
          data.events.forEach(evt => {
            if (evt.tickers) {
              evt.tickers.forEach(ticker => {
                const newPrice = parseFloat(ticker.price);
                if (isNaN(newPrice)) return;
                setPrice(prev => {
                  setPrevPrice(prev);
                  return newPrice;
                });
                setLoading(false);
              });
            }
          });
        } else if (data.type === 'ticker' && data.price) {
          const newPrice = parseFloat(data.price);
          if (isNaN(newPrice)) return;
          setPrice(prev => {
            setPrevPrice(prev);
            return newPrice;
          });
          setLoading(false);
        }
      } catch (err) {

      }
    };

    ws.onerror = () => {};

    ws.onclose = () => {
      wsRef.current = null;
      if (!mountedRef.current) return;

      if (!fallbackTimerRef.current) {
        fetchRest();
        fallbackTimerRef.current = setInterval(fetchRest, REST_POLL_INTERVAL);
      }

      reconnectTimerRef.current = setTimeout(connectWs, WS_RECONNECT_DELAY);
    };
  }, [fetchRest]);

  useEffect(() => {
    mountedRef.current = true;

    fetchRest();
    fetchStats();
    connectWs();

    statsTimerRef.current = setInterval(fetchStats, STATS_POLL_INTERVAL);

    const handleOnline = () => {
      console.group('BTC Price reconnecting');
      console.log('Internet restored');
      console.groupEnd();
      fetchRest();
      fetchStats();
      connectWs();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('BTC Price: tab visible, refreshing data');
        fetchRest();
        fetchStats();
        connectWs();
      }
    };

    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mountedRef.current = false;
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
      if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); }
      if (fallbackTimerRef.current) { clearInterval(fallbackTimerRef.current); }
      if (statsTimerRef.current) { clearInterval(statsTimerRef.current); }
      if (abortRef.current) { abortRef.current.abort(); }
      if (statsAbortRef.current) { statsAbortRef.current.abort(); }
    };
  }, [connectWs, fetchRest, fetchStats]);

  return { price, prevPrice, stats, loading };
};
